import moment from "moment";
import {
  Button,
  Carousel,
  Col,
  Divider,
  Form,
  Image,
  Input,
  Row,
  Select,
} from "antd";
import { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CarouselRef } from "antd/lib/carousel";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";

import sdk from "@/sdk";

import nodataImgUrl from "@/assets/nodata.png";

import { FullLoading } from "../../../components/FullLoading";
import { Status } from "../../../graphql";
import { useGetKYCByUser } from "../../user/modal/useGetKYCByUser";
import { useEditKYC } from "../../user/modal/useEditKYC";
import BlueDivider from "../../../components/BlueDivider";
import { KYCRejectOptions } from "../../../utils/kycRejectOptions";
import { useCanEdit } from "@/hooks/usePermission";

const Option = Select;

function SensitiveDataField({
  label,
  maskedData,
  queryKey,
  queryFn,
}: {
  label: string;
  maskedData?: string;
  queryKey: string[];
  queryFn: () => Promise<string | null>;
}) {
  const [shouldLoadData, setShouldLoadData] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const {
    data: plaintextData,
    isLoading,
    isFetching,
  } = useQuery({ queryKey, queryFn, enabled: shouldLoadData });
  const currentValue = plaintextData ?? maskedData;
  return (
    <div className="form-flex-container-bordered">
      <span>{label} :</span>
      <span>{isRevealed ? currentValue : maskedData}</span>
      <Button
        type="link"
        style={{ marginLeft: "auto" }}
        loading={isLoading && isFetching}
        onClick={() => {
          if (!shouldLoadData) {
            setShouldLoadData(true);
            setIsRevealed(true);
            return;
          }
          setIsRevealed(!isRevealed);
        }}
      >
        {!isRevealed || isLoading ? "Харах" : "Нуух"}
      </Button>
    </div>
  );
}

const KycEditWidget = ({ _id }: { _id: string }) => {
  const history = useHistory();
  const carouselRef = useRef() as React.RefObject<CarouselRef>;
  const canEdit = useCanEdit("kyc");

  const { loading, data, error, form, status, setStatus } = useGetKYCByUser(
    _id ?? null,
  );
  const { loading: editing, editKYC } = useEditKYC({
    selectedKYCId: data?._id ?? null,
  });

  return (
    <>
      <div className="page-header">
        <h3>Хэрэглэгчийн баталгаажуулалт</h3>
        <div className="page-header-buttons">
          <Button
            icon={<ArrowLeftOutlined />}
            className="back-button"
            onClick={() => history.goBack()}
          >
            Буцах
          </Button>
        </div>
      </div>

      {loading && <FullLoading />}
      {error ? (
        <>
          <img width="250" src={nodataImgUrl} alt="no-data" />
          <p>Мэдээлэл олдсонгүй</p>
        </>
      ) : (
        <div className="page-body">
          <Row gutter={24}>
            <Col span={11}>
              <BlueDivider
                text="Хувийн мэдээлэл"
                styles={{ marginBottom: "32px" }}
              />
              <div className="form-flex-container-bordered">
                <span>Овог :</span>

                <span>{data?.user.lastName}</span>
              </div>
              <div className="form-flex-container-bordered">
                <span>Нэр :</span>
                <span>{data?.user.firstName}</span>
              </div>
              <SensitiveDataField
                label="Регистрийн дугаар"
                maskedData={data?.registerNo}
                queryKey={["registerNo", data?._id?.toString() ?? ""]}
                queryFn={() =>
                  sdk
                    .getUserRegisterNo({ kycId: data?._id ?? "" })
                    .then((d) => d.userRegisterNo)
                }
              />
              <div className="form-flex-container-bordered">
                <span>Төрсөн огноо :</span>
                <span>{moment(data?.birthDate).format("YYYY-MM-DD")}</span>
              </div>
              <SensitiveDataField
                label="Гэрийн хаяг"
                maskedData={data?.address}
                queryKey={["address", data?._id?.toString() ?? ""]}
                queryFn={() =>
                  sdk
                    .getUserAddress({ kycId: data?._id ?? "" })
                    .then((d) => d.userAddress)
                }
              />
              <div className="form-flex-container-bordered">
                <span>Утасны дугаар :</span>
                <span>+976 {data?.user.phoneNo}</span>
              </div>
              <div style={{ marginTop: 20 }}>
                <Form
                  form={form}
                  onFinish={editKYC}
                  layout="vertical"
                  autoComplete="off"
                >
                  <Form.Item label="Төлөв өөрчлөх:" name="status">
                    <Select
                      style={{ minWidth: 200, width: "100%" }}
                      disabled={!canEdit || editing}
                      onChange={(value) => canEdit && setStatus(value)}
                    >
                      {Object.values(Status).map((item) => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Тайлбар" name="description">
                    {status === Status.Rejected ? (
                      <Select disabled={!canEdit}>
                        {KYCRejectOptions.map(({ key, description }) => (
                          <Option key={key} value={description}>
                            {description}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Input disabled={!canEdit} />
                    )}
                  </Form.Item>
                </Form>

                <div />
              </div>
              {canEdit && (
                <Button
                  style={{
                    display: data ? "block" : "none",
                    alignSelf: "flex-end",
                  }}
                  key="submit"
                  type="primary"
                  disabled={loading}
                  onClick={() => form.validateFields().then(editKYC)}
                >
                  Хадгалах
                </Button>
              )}
            </Col>
            <Col span={1}>
              <Divider type="vertical" style={{ height: "100%" }}></Divider>
            </Col>
            <Col span={11}>
              <BlueDivider text="Зурагнууд" styles={{ marginBottom: "32px" }} />
              <div
                style={{
                  width: "100%",
                  position: "absolute",
                  left: 0,
                  right: 0,
                }}
              >
                <Carousel dots={false} ref={carouselRef}>
                  {data?.imageUrls?.map((imageUrl) => (
                    <div>
                      <Image
                        height="500px"
                        width="100%"
                        style={{ objectFit: "contain" }}
                        src={imageUrl}
                      />
                    </div>
                  ))}
                </Carousel>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Button
                    type="dashed"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => carouselRef?.current?.prev()}
                  />
                  <Button
                    type="dashed"
                    icon={<ArrowRightOutlined />}
                    onClick={() => carouselRef?.current?.next()}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default KycEditWidget;
